/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import Dropzone from "react-dropzone";
import { Formik } from "formik";

import { GlobalLoaderContext } from "store/context";
import { LoaderContext } from "@fitinsur/contexts";
import { useTextMessage } from "@fitinsur/hooks";
import {
  useGetAdminDocumentParams,
  useSaveAdminDocumentParams,
} from "@fitinsur/services/AdminService";
import { MessageCenter } from "utils";

import { FormContainer, Button, ResponsiveTable } from "@fitinsur/components";
import { ButtonContainer } from "@fitinsur/components/Button";
import Layout from "components/Layout";

import * as Styled from "./styles";
import { events } from "./events";

export function ManagementDocumentParams({ permissions }) {
  const loading = useContext(GlobalLoaderContext);
  const mc = new MessageCenter();

  const { idDomain } = useParams();

  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const { getDescription } = useTextMessage();

  const [file, setFile] = useState(null);
  const [paramsDocument, setParamsDocument] = useState(null);
  const [layoutDocument, setLayoutDocument] = useState(null);

  const [columns, setColumns] = useState([]);
  const [paramsData, setParamsData] = useState([]);
  const [previewParamsData, setPreviewParamsData] = useState([]);
  const [layoutConfig, setLayoutConfig] = useState(null);
  const [errors, setErrors] = useState([]);

  const {
    request: useGetAdminDocumentParamsRequest,
    loading: useGetAdminDocumentParamsLoading,
  } = useGetAdminDocumentParams(idDomain);

  const {
    request: useSaveAdminDocumentParamsRequest,
    loading: useSaveAdminDocumentParamsLoading,
  } = useSaveAdminDocumentParams(idDomain);

  useEffect(() => {
    updateIsLoadingStatus();
    useGetAdminDocumentParamsLoading || useSaveAdminDocumentParamsLoading;
  }, [useGetAdminDocumentParamsLoading, useSaveAdminDocumentParamsLoading]);

  async function getDocumentParamsData() {
    try {
      const response = await useGetAdminDocumentParamsRequest();

      if (response.status === 200) {
        const { value } = response.data;

        setParamsDocument(value.paramsDocument);
        setParamsData(value.paramsDocument.documentMetadata);

        setLayoutDocument(value.layoutDocument);
        setLayoutConfig(value.layoutDocument.documentMetadata);
      }
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    getDocumentParamsData();
  }, []);

  useEffect(() => {
    events.handleColumns({
      layoutConfig,
      mc,
      getDescription,
      setColumns,
      errors,
    });
  }, [paramsData, layoutDocument, layoutConfig, errors]);

  async function saveDocumentParams({ file, isDraft = false }) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("isDraft", isDraft);

      const response = await useSaveAdminDocumentParamsRequest(formData, null, {
        "Content-Type": "multipart/form-data",
      });

      if (response.status === 200) {
        const { value } = response.data;

        if (isDraft) {
          setPreviewParamsData(value.previewParams);

          const errors = value.previewParams
            .filter((item) => item.errors && item.errors.length > 0)
            .map((item) => item.errors);

          if (errors.length > 0) {
            setErrors(errors);

            events.handleColumns({
              layoutConfig,
              mc,
              getDescription,
              setColumns,
              errors,
            });

            mc.error(getDescription("DOMAINS", 1295));
          } else {
            setErrors([]);
            mc.success(getDescription("DOMAINS", 1294));
          }
        } else {
          mc.success(getDescription("DOMAINS", 1290));

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }
    } catch (err) {
      mc.error(getDescription("DOMAINS", 1291));
      
      if (err?.response?.data?.errors && err?.response?.data?.errors.length) {
        err?.response?.data?.errors.forEach((error)=> {
          mc.error(error?.message)
        })
      }
    }
  }

  return (
    <Formik>
      <Layout title={getDescription("DOMAINS", 1292)}>
        {previewParamsData.length > 0 ? (
          <>
            <FormContainer title={getDescription("DOMAINS", 1139)}>
              <ButtonContainer>
                <Styled.ButtonContent>
                  <Button
                    type="button"
                    third
                    onClick={() => {
                      setPreviewParamsData([]);
                      setErrors([]);
                      setFile(null);
                    }}
                  >
                    {getDescription("DOMAINS", 791)}
                  </Button>

                  {errors.length === 0 && (
                    <Button
                      type="submit"
                      primary
                      onClick={() =>
                        saveDocumentParams({
                          file,
                          isDraft: false,
                        })
                      }
                    >
                      {getDescription("DOMAINS", 759)}
                    </Button>
                  )}
                </Styled.ButtonContent>
              </ButtonContainer>
            </FormContainer>

            <ResponsiveTable withHorizontalScroll columns={columns} data={previewParamsData} />
          </>
        ) : (
          <>
            {permissions.EDIT_DOCUMENT_PARAMETERS && (
              <Dropzone
                onDropAccepted={(files) =>
                  events.handleImport({
                    files,
                    setFile,
                    loading,
                    saveDocumentParams,
                  })
                }
                onDropRejected={(event) =>
                  events.handleRejectEvent({ event, mc, getDescription })
                }
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              >
                {({
                  getRootProps,
                  getInputProps,
                  isDragActive,
                  isDragReject,
                }) => (
                  <Styled.DropzoneContainer
                    {...getRootProps()}
                    isDragActive={isDragActive}
                    isDragReject={isDragReject}
                  >
                    <input {...getInputProps()} />
                    {events.renderDragMessage({
                      getDescription,
                      isDragActive,
                      isDragReject,
                    })}
                  </Styled.DropzoneContainer>
                )}
              </Dropzone>
            )}

            <div style={{ marginTop: "20px" }}>
              <ResponsiveTable withHorizontalScroll height="calc(100vh - 450px)" columns={columns} data={paramsData} />
            </div>
          </>
        )}
      </Layout>
    </Formik>
  );
}
